/* =========================================================================
   COMPONENT: ATELIER UNIVERSAL CTA (Builder & Collection Logic)
   ========================================================================= */
class AtelierStickyCTA extends HTMLElement {
  connectedCallback() {
    this.isBuilder = this.classList.contains('atelier-mobile-cta--builder');
    
    // --- 1. DOM TELEPORTATION (Mobile Only Breakout) ---
    this.originalParent = this.parentNode;
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'atelier-cta-placeholder';
    this.originalParent.insertBefore(this.placeholder, this);

    this.relocateDOM = () => {
      const isMobile = window.innerWidth <= 989;
      if (isMobile && this.parentNode !== document.body) {
        document.body.appendChild(this);
      } else if (!isMobile && this.parentNode !== this.originalParent) {
        this.originalParent.insertBefore(this, this.placeholder);
      }
    };

    window.addEventListener('resize', this.relocateDOM, { passive: true });
    this.relocateDOM(); 

    // --- 2. FOOTER COLLISION DETECTION ---
    this.updateFooterOverlap = () => {
      if (!this.footer) {
        this.footer = document.querySelector('footer, .site-footer, .footer, [id*="footer"]');
      }
      if (!this.footer) return;
      
      const footerRect = this.footer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      if (footerRect.top < viewportHeight) {
        this.style.setProperty('--footer-overlap', Math.max(0, viewportHeight - footerRect.top) + 'px');
      } else {
        this.style.setProperty('--footer-overlap', '0px');
      }
    };

    window.addEventListener('scroll', this.updateFooterOverlap, { passive: true });
    window.addEventListener('resize', this.updateFooterOverlap, { passive: true });
    setTimeout(this.updateFooterOverlap, 500); 

    // --- 3. INIT LOGIC ---
    if (this.isBuilder) {
      this.initBuilderLogic();
    } else {
      this.initCollectionLogic();
    }
  }

  // --- MIX & MATCH LOGIC ---
  initBuilderLogic() {
    const staticCta = document.querySelector('.js-static-cta');
    const grid = document.querySelector('.atelier-grid');
    const stickyBtn = this.querySelector('.atelier-add-btn');
    const staticBtn = staticCta ? staticCta.querySelector('.atelier-add-btn') : null;

    const updateUI = () => {
      const state = window.AtelierBuilderState;
      if (!state) return;

      const isComplete = state.totalSelected === state.targetQty;
      const isOverfilled = state.totalSelected > state.targetQty;
      const isEmpty = state.totalSelected === 0;
      
      // Progress Bar Math
      const pct = state.targetQty > 0 ? Math.min((state.totalSelected / state.targetQty) * 100, 100) : 0;
      const rem = Math.max(state.targetQty - state.totalSelected, 0);
      const over = Math.max(state.totalSelected - state.targetQty, 0);
      
      // Calculate Button Text & State
      let btnText = '';
      let btnDisabled = true;

      if (isOverfilled) {
        btnText = 'Remove ' + over + ' to continue';
        btnDisabled = true;
      } else if (isComplete) {
        btnText = 'Add to basket';
        btnDisabled = false;
      } else {
        btnText = 'Select ' + rem + ' more to continue';
        btnDisabled = true;
      }

      // Apply state and progress fill to BOTH buttons
      [stickyBtn, staticBtn].forEach(btn => {
        if (!btn) return;
        btn.style.setProperty('--progress-fill', pct + '%');
        btn.classList.toggle('is-progress', !isComplete); 
        btn.classList.toggle('is-empty', isEmpty);
        btn.disabled = btnDisabled;
        btn.textContent = btnText;
      });

      // Dynamic Visibility Logic (STRICTLY MOBILE NOW)
      let isPastTrigger = false;
      const isMobile = window.innerWidth <= 989;

      if (isMobile && grid) {
        isPastTrigger = grid.getBoundingClientRect().top < 100;
      }

      // Only ever show the sticky bar if we are on a mobile device
      if (isMobile && (isComplete || isPastTrigger)) {
        this.classList.add('is-visible');
      } else {
        this.classList.remove('is-visible');
      }
    };

    window.addEventListener('scroll', updateUI, { passive: true });
    window.addEventListener('resize', updateUI, { passive: true });
    document.addEventListener('click', () => setTimeout(updateUI, 50));
    document.addEventListener('AtelierBuilder:QtyChanged', updateUI); 
    
    setTimeout(updateUI, 100);
  }

  // --- COLLECTION LOGIC ---
  initCollectionLogic() {
    this.triggerSelector = this.dataset.trigger; 
    this.behavior = this.dataset.behavior || 'always';
    this.target = this.triggerSelector ? document.querySelector(this.triggerSelector) : null;

    const updateVisibility = () => {
      const isMobile = window.innerWidth <= 989;
      
      if (!isMobile) {
        this.classList.remove('is-visible');
        return;
      }

      if (this.behavior === 'always') {
        this.classList.add('is-visible');
        return;
      }
    };

    window.addEventListener('resize', updateVisibility, { passive: true });
    updateVisibility();

    if (!this.target || this.behavior === 'always') return;

    this.observer = new IntersectionObserver((entries) => {
      const isMobile = window.innerWidth <= 989;
      if (!isMobile) return; // Ignore observer on desktop

      entries.forEach((entry) => {
        const isAboveViewport = entry.boundingClientRect.top < 0;
        const isBelowViewport = entry.boundingClientRect.top > 0;

        if (this.behavior === 'show-after') {
          if (!entry.isIntersecting && isAboveViewport) this.classList.add('is-visible');
          else this.classList.remove('is-visible');
        } else if (this.behavior === 'show-before') {
          if (!entry.isIntersecting && isBelowViewport) this.classList.add('is-visible');
          else this.classList.remove('is-visible');
        }
      });
    }, { threshold: 0 });

    this.observer.observe(this.target);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateFooterOverlap);
    window.removeEventListener('resize', this.updateFooterOverlap);
    window.removeEventListener('resize', this.relocateDOM);
    if (this.observer) this.observer.disconnect();
  }
}

if (!customElements.get('atelier-sticky-cta')) {
  customElements.define('atelier-sticky-cta', AtelierStickyCTA);
}